import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Calendar, ArrowRight, BookOpen } from 'lucide-react';
import useSEO from '@/hooks/useSEO';

const BlogPage = () => {
  useSEO('blog', 'Blog | We Have Your Car');
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBlogPosts();
  }, []);

  const fetchBlogPosts = async () => {
    try {
      const response = await axios.get(`${API}/blog-posts`);
      setPosts(response.data);
    } catch (error) {
      console.error('Error fetching blog posts:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a1628] flex items-center justify-center">
        <div className="animate-pulse text-white">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a1628]" data-testid="blog-page">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-[#132238] to-[#1a4d8c]/30 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4 font-['Exo_2']">
            <span className="gradient-text">Blog</span> & News
          </h1>
          <p className="text-xl text-[#a8b8d0] max-w-2xl">
            Stay updated with the latest car buying tips, industry news, and dealership updates.
          </p>
        </div>
      </div>

      {/* Blog Posts Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {posts.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {posts.filter(post => post.is_published).map((post) => (
              <article
                key={post.id}
                className="bg-[#132238] rounded-xl overflow-hidden border border-[#1a2d47] hover:border-[#1a4d8c] transition-colors group"
              >
                {post.featured_image && (
                  <div className="h-48 overflow-hidden">
                    <img
                      src={post.featured_image}
                      alt={post.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                )}
                <div className="p-6">
                  <div className="flex items-center gap-2 text-[#a8b8d0] text-sm mb-3">
                    <Calendar size={14} />
                    <span>{new Date(post.published_date || post.created_at).toLocaleDateString('en-US', { 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}</span>
                  </div>
                  <h2 className="text-xl font-bold text-white mb-3 group-hover:text-[#ff6b35] transition-colors">
                    {post.title}
                  </h2>
                  <p className="text-[#a8b8d0] text-sm mb-4 line-clamp-3">
                    {post.excerpt || post.content?.substring(0, 150) + '...'}
                  </p>
                  <Link
                    to={`/blog/${post.slug || post.id}`}
                    className="inline-flex items-center gap-2 text-[#ff6b35] hover:text-[#ff8c5a] font-medium text-sm transition-colors"
                  >
                    Read More
                    <ArrowRight size={16} />
                  </Link>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <BookOpen size={64} className="text-[#a8b8d0] mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-white mb-2">No Blog Posts Yet</h2>
            <p className="text-[#a8b8d0] mb-6">
              Check back soon for car buying tips, industry news, and more!
            </p>
            <Link to="/inventory" className="btn-accent inline-flex items-center gap-2">
              Browse Our Inventory
              <ArrowRight size={18} />
            </Link>
          </div>
        )}
      </div>

      {/* CTA Section */}
      <div className="bg-[#132238] py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl font-bold text-white mb-4">Ready to Find Your Next Car?</h2>
          <p className="text-[#a8b8d0] mb-6">
            Browse our quality selection of used vehicles with flexible financing options.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/inventory" className="btn-accent">
              View Inventory
            </Link>
            <Link to="/credit-application" className="btn-primary">
              Apply for Financing
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogPage;
